import { useCallback, useEffect, useRef, useState } from "react";
import { Nullable, TriggeredHook } from "sonobello.utilities.react";

import EnvironmentConfiguration from "../../../../constants/EnvironmentConfiguration";
import { HttpStatusCode } from "../../../../types/HttpStatusCodes";
import { useObx } from "../../../../utils/UseObx";
import CalendarSlotStatus from "../../../Calendar/Types/CalendarSlotStatus";
import { ICalendarService, IScheduleSlot } from "../../../Calendar/Types/ICalendar";
import { OpsScheduleSlot } from "../../../Calendar/Types/OpsCalendar";
import ApiRequestPaths from "../../../Constants/ApiRequestPaths";
import ICenter from "../../../Types/ICenter";
import IReservation from "../../../Types/IReservation";
import OpsReservation from "../../../Types/OpsReservation";

export interface IUseCreateOpsReservationProps {
  /** The id of the current session's lead. */
  leadId: string;
  /** The center in which the reservation should be made. */
  center: ICenter;
  /** The service which will be conducted at the reserved appointment. */
  service: ICalendarService;
  /** The id of the customer associated with the reservation to be made. */
  customerId: string;
}

export class ReservationRequestWithDistance {
  /** The start time of the block in utc. */
  startTimeUtc: string;
  /** The end time of the block in utc. */
  endTimeUtc: string;
  /** The state of block being described by this object. */
  type: CalendarSlotStatus;
  /** The distance in miles from the customer to the center. */
  distanceToCenterInMiles: Nullable<number>;

  constructor(
    slot: Pick<IScheduleSlot, "startTimeUtc" | "endTimeUtc">,
    calendarSlotStatus?: CalendarSlotStatus,
    distanceToCenterInMiles?: Nullable<number>
  ) {
    this.startTimeUtc = slot.startTimeUtc.toISO();
    this.endTimeUtc = slot.endTimeUtc.toISO();
    this.type = calendarSlotStatus ?? CalendarSlotStatus.Reserved;
    this.distanceToCenterInMiles =
      distanceToCenterInMiles ?? EnvironmentConfiguration.optimizedPatientScheduler.fallbackCustomerDistanceToCenter;
  }
}

/** Handles the network requests necessary to make reservation requests for the OPS calendar process and
 * passes the customer's distance to center along with the request.
 */
const useCreateOpsDistanceReservation: TriggeredHook<
  IReservation,
  OpsScheduleSlot,
  boolean,
  IUseCreateOpsReservationProps
> = props => {
  const propsRef = useRef<IUseCreateOpsReservationProps>(props);
  const [result, setResult] = useState<Nullable<IReservation>>(null);

  useEffect(() => {
    propsRef.current = props;
  }, [props]);

  const { res, err, loading, setReq } = useObx<
    void,
    ReservationRequestWithDistance,
    { service: ICalendarService; slot: OpsScheduleSlot }
  >("Post Reservation", { method: "post" });

  useEffect(() => {
    if (res?.code !== HttpStatusCode.Accepted) return;
    setResult(new OpsReservation(propsRef.current.center, res.request.custom!.service, res.request.custom!.slot));
  }, [res]);

  const execute = useCallback(
    (slotToReserve: OpsScheduleSlot) =>
      setReq(
        r =>
          r && {
            ...r,
            url: ApiRequestPaths.postOpsReservationUrlV1_1(
              propsRef.current.leadId,
              propsRef.current.customerId,
              propsRef.current.center.id,
              propsRef.current.service.id,
              slotToReserve.id
            ),
            payload: new ReservationRequestWithDistance(slotToReserve, undefined, propsRef.current.center.distance),
            custom: {
              service: propsRef.current.service,
              slot: slotToReserve
            }
          }
      ),
    []
  );

  return {
    result,
    isLoading: loading,
    error: Boolean(err && err.error.response?.status !== HttpStatusCode.Unauthorized),
    execute
  };
};

export default useCreateOpsDistanceReservation;
